import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { SlidersHorizontal, Loader2 } from 'lucide-react';
import FilterAccordion from './FilterAccordion';
import ColorCard from './ColorCard';
import ColorModal from './ColorModal';
import { FILTERS, PALETTE, DOOR_STYLE_KEYS } from '@/data/palette';

const PAGE_SIZE = 16;

const initialSelected = {
  colorFamily: [],
  colorPattern: [],
  specialFeatures: [],
  texture: [],
  doorStyle: [],
  price: [],
};

const PaletteApp = () => {
  const [selected, setSelected] = useState(initialSelected);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [activeItem, setActiveItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const sentinelRef = useRef(null);

  const handleToggle = (group, value) => {
    setSelected((prev) => {
      const arr = prev[group];
      const next = arr.includes(value)
        ? arr.filter((v) => v !== value)
        : [...arr, value];
      return { ...prev, [group]: next };
    });
    setVisible(PAGE_SIZE); // reset when filter changes
  };

  const handleClear = () => {
    setSelected(initialSelected);
    setVisible(PAGE_SIZE);
  };

  const filtered = useMemo(() => {
    return PALETTE.filter((item) => {
      if (selected.colorFamily.length && !selected.colorFamily.includes(item.family)) return false;
      if (selected.colorPattern.length && !selected.colorPattern.includes(item.pattern)) return false;
      if (selected.texture.length && !selected.texture.includes(item.texture)) return false;
      if (selected.price.length && !selected.price.includes(item.price)) return false;
      if (selected.specialFeatures.length) {
        const has = selected.specialFeatures.some((f) => item.features.includes(f));
        if (!has) return false;
      }
      if (selected.doorStyle.length) {
        const has = selected.doorStyle.some((d) => {
          const key = DOOR_STYLE_KEYS[d];
          return key && item.doors[key];
        });
        if (!has) return false;
      }
      return true;
    });
  }, [selected]);

  const visibleItems = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!hasMore) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible((v) => v + PAGE_SIZE);
          }
        });
      },
      { rootMargin: '300px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, filtered.length]);

  const openItem = (item) => {
    setActiveItem(item);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Page header */}
      <div className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <h1 className="font-serif text-[34px] md:text-[44px] leading-tight text-neutral-900 tracking-tight">
            Color Palette
          </h1>
          <p className="mt-2 text-[15px] text-neutral-600 max-w-2xl">
            Explore our complete selection of finishes. Use the filters to narrow by
            family, pattern, texture, price and door style.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10 pb-24 md:pb-32">
        <div className="flex gap-8">
          {/* Desktop filter sidebar */}
          <div className="hidden lg:block">
            <FilterAccordion
              filters={FILTERS}
              selected={selected}
              onToggle={handleToggle}
              onClear={handleClear}
            />
          </div>

          {/* Main grid */}
          <main className="flex-1 min-w-0">
            {/* Top bar (mobile filter button + result count) */}
            <div className="flex items-center justify-between mb-5">
              <div className="lg:hidden">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-neutral-300 text-neutral-800 hover:bg-neutral-50 rounded-sm"
                    >
                      <SlidersHorizontal className="w-4 h-4 mr-2" />
                      Filters
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-[320px] overflow-y-auto">
                    <div className="pt-6">
                      <FilterAccordion
                        filters={FILTERS}
                        selected={selected}
                        onToggle={handleToggle}
                        onClear={handleClear}
                      />
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
              <p className="text-[13px] text-neutral-500 ml-auto">
                Showing{' '}
                <span className="font-semibold text-neutral-800">
                  {visibleItems.length}
                </span>{' '}
                of{' '}
                <span className="font-semibold text-neutral-800">
                  {filtered.length}
                </span>{' '}
                colors
              </p>
            </div>

            {filtered.length === 0 ? (
              <div className="py-20 text-center">
                <p className="text-neutral-600 text-[15px]">
                  No colors match the selected filters.
                </p>
                <button
                  type="button"
                  onClick={handleClear}
                  className="mt-3 text-[#6BA797] hover:text-[#4f8676] text-[13px] uppercase tracking-wider font-semibold"
                >
                  Remove filters
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-8">
                  {visibleItems.map((item) => (
                    <ColorCard key={item.id} item={item} onClick={openItem} />
                  ))}
                </div>

                {hasMore && (
                  <div
                    ref={sentinelRef}
                    className="flex items-center justify-center py-10 text-neutral-500"
                  >
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    <span className="text-[13px]">Loading more colors…</span>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      <ColorModal item={activeItem} open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
};

export default PaletteApp;
